import { useState } from "react";
import { Card, Field, TextInput, TextArea, Toggle, SaveButton } from "./kit";

export function HeroManager({ initial, onSave, saving }) {
  const [d, setD] = useState(initial);
  if (!initial) return null;
  const value = d || initial;
  return (
    <Card title="Hero Manager" desc="Edit the headline, subtext, laptop card titles and urgency line.">
      <Field label="Eyebrow label">
        <TextInput value={value.eyebrow || ""} onChange={(e) => setD({ ...value, eyebrow: e.target.value })} />
      </Field>
      <Field label="Headline">
        <TextInput value={value.headline || ""} onChange={(e) => setD({ ...value, headline: e.target.value })} />
      </Field>
      <Field label="Subtext">
        <TextArea value={value.subtext || ""} onChange={(e) => setD({ ...value, subtext: e.target.value })} />
      </Field>
      <div className="grid sm:grid-cols-3 gap-3">
        {(value.cards || ["", "", ""]).map((c, i) => (
          <Field key={i} label={`Laptop card ${i + 1}`}>
            <TextInput value={c} onChange={(e) => { const cards = [...(value.cards || [])]; cards[i] = e.target.value; setD({ ...value, cards }); }} />
          </Field>
        ))}
      </div>
      <Field label="Urgency text">
        <TextInput value={value.urgency_text || ""} onChange={(e) => setD({ ...value, urgency_text: e.target.value })} />
      </Field>
      <Toggle
        label="Show urgency text"
        checked={value.urgency_enabled !== false}
        onChange={(v) => setD({ ...value, urgency_enabled: v })}
      />
      <SaveButton onClick={() => onSave("hero", value)} saving={saving} />
    </Card>
  );
}

export function TalentManager({ initial, onSave, saving }) {
  const [d, setD] = useState(null);
  if (!initial) return null;
  const value = d || initial;
  const cats = value.categories || [];
  return (
    <Card title="Talent Pool Manager" desc="Edit category names and specialist counts. Changes go live immediately.">
      <div className="space-y-3">
        {cats.map((c, i) => (
          <div key={i} className="flex gap-3 items-center">
            <TextInput value={c.name} onChange={(e) => { const next = [...cats]; next[i] = { ...c, name: e.target.value }; setD({ ...value, categories: next }); }} />
            <div className="w-28 flex-shrink-0">
              <TextInput type="number" value={c.count} onChange={(e) => { const next = [...cats]; next[i] = { ...c, count: Number(e.target.value) }; setD({ ...value, categories: next }); }} />
            </div>
            <button className="text-slate-500 hover:text-red-400 transition-colors px-2" onClick={() => setD({ ...value, categories: cats.filter((_, j) => j !== i) })} aria-label="Remove category">✕</button>
          </div>
        ))}
      </div>
      <button className="mt-4 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors" onClick={() => setD({ ...value, categories: [...cats, { name: "New Category", count: 0 }] })}>
        + Add category
      </button>
      <SaveButton onClick={() => onSave("talent", value)} saving={saving} />
    </Card>
  );
}

export function ProcessManager({ initial, onSave, saving }) {
  const [d, setD] = useState(null);
  if (!initial) return null;
  const value = d || initial;
  const steps = value.steps || [];
  return (
    <Card title="Process Manager" desc="Edit the four process step titles and descriptions.">
      <div className="space-y-5">
        {steps.map((s, i) => (
          <div key={i} className="rounded-xl border border-white/10 p-4 bg-[#0A0F2C]/40">
            <div className="eyebrow text-[9px] text-cyan-400 mb-3">Step {String(i + 1).padStart(2, "0")}</div>
            <Field label="Title">
              <TextInput value={s.title} onChange={(e) => { const next = [...steps]; next[i] = { ...s, title: e.target.value }; setD({ ...value, steps: next }); }} />
            </Field>
            <Field label="Description">
              <TextArea value={s.description} onChange={(e) => { const next = [...steps]; next[i] = { ...s, description: e.target.value }; setD({ ...value, steps: next }); }} />
            </Field>
          </div>
        ))}
      </div>
      <SaveButton onClick={() => onSave("process", value)} saving={saving} />
    </Card>
  );
}

export function PackagesManager({ initial, onSave, saving }) {
  const [d, setD] = useState(null);
  if (!initial) return null;
  const value = d || initial;
  const pkgs = value.packages || [];
  const setPkg = (i, next) => { const arr = [...pkgs]; arr[i] = next; setD({ ...value, packages: arr }); };
  return (
    <Card title="Packages Manager" desc="Edit package names, descriptions, included items and the recommended badge.">
      <div className="space-y-5">
        {pkgs.map((p, i) => (
          <div key={i} className="rounded-xl border border-white/10 p-4 bg-[#0A0F2C]/40">
            <div className="flex items-center justify-between gap-4">
              <Field label="Name" className="flex-1">
                <TextInput value={p.name} onChange={(e) => setPkg(i, { ...p, name: e.target.value })} />
              </Field>
              <Toggle label="Recommended" checked={!!p.recommended} onChange={(v) => setPkg(i, { ...p, recommended: v })} />
            </div>
            <Field label="Description">
              <TextArea value={p.description} onChange={(e) => setPkg(i, { ...p, description: e.target.value })} />
            </Field>
            <Field label="Included items (one per line)">
              <TextArea value={(p.items || []).join("\n")} onChange={(e) => setPkg(i, { ...p, items: e.target.value.split("\n").filter(Boolean) })} />
            </Field>
          </div>
        ))}
      </div>
      <SaveButton onClick={() => onSave("packages", value)} saving={saving} />
    </Card>
  );
}

export function PromoManager({ initial, onSave, saving }) {
  const [d, setD] = useState(null);
  if (!initial) return null;
  const value = d || initial;
  return (
    <Card title="Promotions Manager" desc="Edit the pop-up shown to visitors after 60 seconds (once per session).">
      <Toggle label="Pop-up enabled" checked={value.enabled !== false} onChange={(v) => setD({ ...value, enabled: v })} />
      <Field label="Headline">
        <TextInput value={value.headline || ""} onChange={(e) => setD({ ...value, headline: e.target.value })} />
      </Field>
      <Field label="Message">
        <TextArea value={value.message || ""} onChange={(e) => setD({ ...value, message: e.target.value })} />
      </Field>
      <Field label="CTA text">
        <TextInput value={value.cta_text || ""} onChange={(e) => setD({ ...value, cta_text: e.target.value })} />
      </Field>
      <SaveButton onClick={() => onSave("promo", value)} saving={saving} />
    </Card>
  );
}
